import { z } from "zod";

import { REPORT_PERIODS, REPORT_RANGE_MAX_DAYS, daysInRange } from "@pharmacy/domain";
import { moneyText } from "../money";

/**
 * The Reports tab: sales for a day, a week or a month, profit, the most-sold
 * medicines, dead stock, and — for owners — what each person did.
 *
 * Every report covers all the branches the person works in, or one of them
 * when `branchId` is given.
 */

const isoDay = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Not a date");

/**
 * Either a period and a day inside it ("this week"), or any From and To
 * ("Choose dates", up to two years). From and To win when both are given.
 */
export const salesReportQuery = z
  .object({
    period: z.enum(REPORT_PERIODS).optional(),
    /** Any day inside the period. */
    date: isoDay.optional(),
    /** The first and last day, both included. */
    from: isoDay.optional(),
    to: isoDay.optional(),
    branchId: z.string().uuid().optional(),
  })
  .superRefine((q, issues) => {
    if (q.from || q.to) {
      if (!q.from || !q.to) {
        issues.addIssue({ code: "custom", path: [q.from ? "to" : "from"], message: "Give both From and To" });
      } else if (q.from > q.to) {
        issues.addIssue({ code: "custom", path: ["to"], message: "To is before From" });
      } else if (daysInRange(q.from, q.to) > REPORT_RANGE_MAX_DAYS) {
        issues.addIssue({ code: "custom", path: ["to"], message: "At most two years at a time" });
      }
    } else if (!q.period || !q.date) {
      issues.addIssue({ code: "custom", path: ["period"], message: "Give a period and a date, or From and To" });
    }
  });

/** One medicine in Most sold: how many, and the revenue it brought in. */
const mostSoldMedicine = z.object({
  name: z.string(),
  strength: z.string().nullable(),
  baseUnit: z.string(),
  qtyBase: z.number().int(),
  total: moneyText,
});

export const salesReportResponse = z.object({
  /** The first and last day covered, both included. */
  from: z.string(),
  to: z.string(),
  /** Sales that stand: cancelled ones left out, an edited one counted once. */
  salesCount: z.number().int(),
  /** What was sold, at the prices charged: the revenue. */
  total: moneyText,
  /**
   * What came in on each wallet, biggest first: sales paid there, and credit
   * paid back there — everything that should be in that wallet.
   */
  byWallet: z.array(z.object({ method: z.string(), amount: moneyText })),
  /** Sold on credit (deyn): part of the sales, but no money in yet. */
  givenOnCredit: moneyText,
  /** Credit paid back in the period. Already counted in `byWallet`. */
  creditPaidBack: moneyText,
  /**
   * The part of `creditPaidBack` that paid old credit — added on the Credits
   * page with no sale (2026-10-18). Revenue that cost nothing: in `profit`'s
   * sales, never in `total`. Optional: older servers did not send it.
   */
  oldCreditPaid: moneyText.optional(),
  /**
   * What the medicines sold cost the shop, from each batch's own cost, and
   * the difference. Null for somebody not allowed to see cost prices.
   */
  profit: z
    .object({
      cost: moneyText,
      sales: moneyText,
      profit: moneyText,
    })
    .nullable(),
  /** The top ten by quantity, in the unit each is counted in. */
  mostSold: z.array(mostSoldMedicine),
  /** How many medicines sold at all: "View more" shows when it is over ten. */
  mostSoldCount: z.number().int(),
});

/** Every medicine sold in the period, most first. */
export const mostSoldResponse = z.object({
  from: z.string(),
  to: z.string(),
  medicines: z.array(mostSoldMedicine),
});

export const deadStockQuery = z.object({ branchId: z.string().uuid().optional() });

export const deadStockResponse = z.object({
  /** Unsold for this many days. */
  days: z.number().int(),
  medicines: z.array(
    z.object({
      /** One of the medicine's ids, to open its page. */
      productId: z.string().uuid(),
      name: z.string(),
      strength: z.string().nullable(),
      baseUnit: z.string(),
      qtyBase: z.number().int(),
      /** What the stock left cost. Null for somebody not allowed to see cost prices. */
      value: moneyText.nullable(),
      /** Null for never sold. */
      lastSoldAt: z.string().nullable(),
    }),
  ),
});

export const activityKind = z.enum([
  "sale_created",
  "sale_edited",
  "sale_cancelled",
  "stock_recorded",
  "stock_changed",
  "cost_corrected",
  "payment_recorded",
  "medicine_added",
  "medicine_changed",
  "medicine_archived",
  "refused_sale_removed",
]);

export const activityQuery = z.object({
  branchId: z.string().uuid().optional(),
  userId: z.string().uuid().optional(),
  /** One day only, in the pharmacy's own calendar: what was done that day. */
  date: isoDay.optional(),
  /** `next` from the page before. */
  before: z.string().max(80).optional(),
});

export const activityResponse = z.object({
  entries: z.array(
    z.object({
      id: z.string().uuid(),
      /** When the server recorded it. */
      at: z.string(),
      /** For a sale: when it was made — earlier than `at` for one made offline. */
      soldAt: z.string().nullable(),
      userName: z.string(),
      kind: activityKind,
      receiptNo: z.string().nullable(),
      total: moneyText.nullable(),
      /** For a payment towards deyn: whose, and on which wallet. */
      customerName: z.string().nullable(),
      method: z.string().nullable(),
      productName: z.string().nullable(),
      baseUnit: z.string().nullable(),
      /** Stock recorded (positive) or changed by hand (either sign), in the smallest unit. */
      qtyBase: z.number().int().nullable(),
      /** A corrected cost, per smallest unit: what it was, and what it is now. */
      costFrom: moneyText.nullable(),
      costTo: moneyText.nullable(),
      /** The one-tap reason, as its code: "wrong_price", "missing". */
      reason: z.string().nullable(),
      note: z.string().nullable(),
    }),
  ),
  /** Send as `before` for the next page; null at the end. */
  next: z.string().nullable(),
  /** Everybody who can be picked, for the person filter. */
  people: z.array(z.object({ id: z.string().uuid(), name: z.string() })),
});

/* ------------------------------------------------------- day summaries */

/**
 * One branch's sales on one day, added up — what a phone keeps so that every
 * report, for any days, works with no internet. Only those that stand: not
 * cancelled, an edited sale once as its latest version.
 */
export const reportDay = z.object({
  branchId: z.string().uuid(),
  /** In the pharmacy's own calendar. */
  day: isoDay,
  salesCount: z.number().int(),
  total: moneyText,
  /** What was sold cost, at each batch's cost. Null for somebody not allowed to see cost prices. */
  cost: moneyText.nullable(),
  /** Money in, by wallet: what sales took, and what credit customers paid back. Deyn is not money in. */
  wallets: z.array(z.object({ method: z.string(), amount: moneyText })),
  givenOnCredit: moneyText,
  creditPaidBack: moneyText,
  /** Old credit (no sale) paid back that day: revenue that cost nothing. Optional: older servers. */
  oldCreditPaid: moneyText.optional(),
  /** Each medicine sold that day, by its id: see `products` for its name. */
  lines: z.array(z.object({ productId: z.string().uuid(), qtyBase: z.number().int(), total: moneyText })),
});

/** The days changed since the phone last asked; every day, the first time. */
export const reportDaysQuery = z.object({
  since: z
    .string()
    .max(40)
    .refine((v) => !Number.isNaN(Date.parse(v)), "Not a time")
    .optional(),
});

export const reportDaysResponse = z.object({
  /** Ask with this next time. */
  cursor: z.string(),
  /** Whether `cost` is filled in for this person. */
  seesCost: z.boolean(),
  /** The branches these days cover: every branch this person works in. */
  branchIds: z.array(z.string().uuid()),
  /** Whole days: a day sent again replaces the phone's. */
  days: z.array(reportDay),
  /** Names for the medicines in `days`, and for any renamed since: kept by id. */
  products: z.array(
    z.object({ id: z.string().uuid(), name: z.string(), strength: z.string().nullable(), baseUnit: z.string() }),
  ),
});

export type ReportDay = z.infer<typeof reportDay>;
export type ReportDaysQuery = z.infer<typeof reportDaysQuery>;
export type ReportDaysResponse = z.infer<typeof reportDaysResponse>;
export type SalesReportQuery = z.infer<typeof salesReportQuery>;
export type SalesReportResponse = z.infer<typeof salesReportResponse>;
export type MostSoldResponse = z.infer<typeof mostSoldResponse>;
export type DeadStockQuery = z.infer<typeof deadStockQuery>;
export type DeadStockResponse = z.infer<typeof deadStockResponse>;
export type ActivityKind = z.infer<typeof activityKind>;
export type ActivityQuery = z.infer<typeof activityQuery>;
export type ActivityResponse = z.infer<typeof activityResponse>;
